import React from 'react';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, SignIn, SignUp, useUser } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Dashboard from './pages/Dashboard';
import DashboardNew from './pages/DashboardNew';
import NewClaim from './pages/NewClaim';
import Wizard from './pages/Wizard';
import WizardV2Page from './pages/WizardV2';
import ReportDetail from './pages/ReportDetail';
import NarratorPage from './pages/NarratorPage';
import DemoMode from './pages/DemoMode';
import DemoNarratorPage from './pages/DemoNarratorPage';
import DemoWizard from './pages/DemoWizard';
import DemoReportDetail from './pages/DemoReportDetail';
import CSVFeaturesDemo from './pages/CSVFeaturesDemo';
import LandingPage from './pages/LandingPage';
import NotFound from './pages/NotFound';
import BulkUpload from './pages/BulkUpload';
import CompareReports from './pages/CompareReports';
import AccountantLogin from './pages/AccountantLogin';
import AccountantRegister from './pages/AccountantRegister';
import AccountantDashboard from './pages/AccountantDashboard';
import AccountantReportView from './pages/AccountantReportView';
import AccountantProfilePage from './pages/AccountantProfilePage';
import AccountantSharedReports from './pages/AccountantSharedReports';
import AccountantComments from './pages/AccountantComments';
import AccountantReconciliation from './pages/AccountantReconciliation';
import AccountantAnomalyReview from './pages/AccountantAnomalyReview';
import AcceptInvite from './pages/AcceptInvite';
import CleanupValidation from './pages/CleanupValidation';
import CleanupJobs from './pages/CleanupJobs';
import CleanupMonitoring from './pages/CleanupMonitoring';
import PublicShareView from './pages/PublicShareView';
import CleanupHistory from './pages/CleanupHistory';
import SettingsPage from './pages/SettingsPage';
import AdminActivityLog from './pages/AdminActivityLog';
import StyleDemo from './pages/StyleDemo';
import AnalyticsPage from './pages/AnalyticsPage';
import VendorIntelligencePage from './pages/VendorIntelligencePage';
import GameTheoryPage from './pages/GameTheoryPage';
import SettlementIntelligencePage from './pages/SettlementIntelligencePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import WaitlistPage from './pages/WaitlistPage';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DocsGuidePage from './pages/DocsGuidePage';
import UnifiedUploadPage from './pages/UnifiedUploadPage';
import DataReviewPage from './pages/DataReviewPage';
import ClosedCasesPage from './pages/ClosedCasesPage';
import TransactionReviewQueue from './pages/TransactionReviewQueue';
import ClientUploadPortal from './pages/ClientUploadPortal';
import ClientClarificationPortal from './pages/ClientClarificationPortal';
import UnifiedClientPortal from './pages/UnifiedClientPortal';
import { UploadPortalRouter, ClarificationPortalRouter } from './components/routing/ClientPortalRouter';
import { DataSpecialistQueueRouter, TransactionReviewRouter, IssueTrackerRouter } from './components/routing/DataSpecialistRouter';
import UnifiedWorkQueue from './pages/UnifiedWorkQueue';
import BatchProcessingPage from './pages/BatchProcessingPage';
import { AccountantAuthProvider } from './contexts/AccountantAuthContext';
import { setAuthTokenGetter } from './utils/api';
import { useAuth } from '@clerk/clerk-react';
import AttorneyLogin from './pages/AttorneyLogin';
import AttorneyDashboard from './pages/AttorneyDashboard';
import AttorneyCaseDetail from './pages/AttorneyCaseDetail';
import AttorneySettlementTools from './pages/AttorneySettlementTools';
import AttorneyAdvancedTools from './pages/AttorneyAdvancedTools';
import AttorneyResetPassword from './pages/AttorneyResetPassword';
import AttorneyPackageView from './pages/AttorneyPackageView';
import DemandLetterGenerator from './pages/DemandLetterGenerator';
import BITimelinePage from './pages/BITimelinePage';
// PolicyDNAPage removed - policy coverage now integrated into AttorneyCaseDetail
import IssueTrackerDashboard from './pages/IssueTrackerDashboard';
import IssueDetail from './pages/IssueDetail';
import AdjusterAnalyzerView from './pages/AdjusterAnalyzerView';
import OrchestratorDashboard from './pages/OrchestratorDashboard';
import StaffRoadmapPage from './pages/StaffRoadmapPage';
import DataSpecialistQueue from './pages/DataSpecialistQueue';
import PatternAdminPage from './pages/PatternAdminPage';
import AccountantQueue from './pages/AccountantQueue';
const AttorneyQueue = React.lazy(() => import('./pages/AttorneyQueue'));
import StaffDashboard from './pages/StaffDashboard';
import DataSpecialistDashboard from './pages/dashboards/DataSpecialistDashboard';
import AccountantDashboardRole from './pages/dashboards/AccountantDashboard';
import { BrandingProvider } from './contexts/BrandingContext';
import { TestAuthProvider, isTestMode, TestSignedIn, TestSignedOut } from './contexts/TestAuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { useLocation, useParams } from 'react-router-dom';

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || 'pk_test_bWFnbmV0aWMtc2hlZXAtNDUuY2xlcmsuYWNjb3VudHMuZGV2JA';

// Redirect component that preserves query parameters
function WizardRedirect() {
  const location = useLocation();
  // Preserve query params when redirecting from /wizard to /wizard-v2
  const newPath = `/wizard-v2${location.search}`;
  return <Navigate to={newPath} replace />;
}

// Redirect old settlement tools route to case detail with hash
function SettlementToolsRedirect() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/attorney/case/${caseId}#sensitivity`} replace />;
}

// Redirect old advanced tools route to case detail with hash
function AdvancedToolsRedirect() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/attorney/case/${caseId}#bad-faith`} replace />;
}

// Component that shows HomePage for non-authenticated users, redirects to dashboard for authenticated
function ConditionalHome() {
  if (isTestMode) {
    // In test mode, user is always "logged in", go to dashboard
    return <Navigate to="/dashboard" replace />;
  }
  // In production, show homepage for signed out, dashboard for signed in
  return (
    <>
      <SignedIn><Navigate to="/dashboard" replace /></SignedIn>
      <SignedOut><HomePage /></SignedOut>
    </>
  );
}

// Wrapper components that use test mode or Clerk based on environment
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (isTestMode) {
    return <>{children}</>;
  }
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut><Navigate to="/sign-in" replace /></SignedOut>
    </>
  );
}

// Component to register the auth token getter for the API utility
function AuthTokenRegistrar({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded } = useAuth();

  React.useEffect(() => {
    if (isLoaded) {
      // Register the Clerk getToken function with the API utility
      setAuthTokenGetter(getToken);
      console.log('Clerk auth loaded, token getter registered');
    }
  }, [isLoaded, getToken]);

  return <>{children}</>;
}

function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public marketing pages - no auth required */}
        <Route path="/home" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        
        {/* Legacy landing page */}
        <Route path="/landing" element={<LandingPage />} />
        
        {/* Public Share View - No auth required */}
        <Route path="/share/:shareToken" element={<PublicShareView />} />
        
        {/* Public demo routes */}
        <Route path="/demo" element={<DemoMode />} />
        <Route path="/demo-narrator" element={<DemoNarratorPage />} />
        <Route path="/demo-wizard" element={<DemoWizard />} />
        <Route path="/demo-report/:id" element={<DemoReportDetail />} />
        <Route path="/csv-features" element={<CSVFeaturesDemo />} />
        
        {/* Style Demo - No auth required */}
        <Route path="/style-demo" element={<StyleDemo />} />
        
        {/* Public Upload Portal - Feature flag aware (redirects to unified when enabled) */}
        <Route path="/upload/:reportId" element={<UploadPortalRouter />} />
        
        {/* Public Clarification Portal - Feature flag aware (redirects to unified when enabled) */}
        <Route path="/clarify/:token" element={<ClarificationPortalRouter />} />
        
        {/* Unified Client Portal (Documents + Clarifications) - No auth required */}
        <Route path="/portal/:token" element={<UnifiedClientPortal />} />
        
        {/* Waitlist page - always accessible */}
        <Route path="/waitlist" element={<WaitlistPage />} />
        
        {/* Sign in/up routes - skip in test mode */}
        {!isTestMode && (
          <>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
          </>
        )}
        
        {/* In test mode, redirect sign-in to dashboard */}
        {isTestMode && (
          <>
            <Route path="/sign-in" element={<Navigate to="/" replace />} />
            <Route path="/sign-up" element={<Navigate to="/" replace />} />
          </>
        )}
        
        {/* Root route - shows homepage for non-auth, dashboard for auth */}
        <Route path="/" element={<ConditionalHome />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardNew /></ProtectedRoute>} />
        <Route path="/new-claim" element={<ProtectedRoute><NewClaim /></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><UnifiedUploadPage /></ProtectedRoute>} />
        <Route path="/dashboard-old" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        {/* Old wizard redirects to new wizard-v2 (preserves query params) */}
        <Route path="/wizard" element={<WizardRedirect />} />
        <Route path="/wizard-v2" element={<ProtectedRoute><WizardV2Page /></ProtectedRoute>} />
        <Route path="/report/:id" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
        <Route path="/compare" element={<ProtectedRoute><CompareReports /></ProtectedRoute>} />
        <Route path="/narrator/:id" element={<ProtectedRoute><NarratorPage /></ProtectedRoute>} />
        <Route path="/bulk-upload" element={<ProtectedRoute><BulkUpload /></ProtectedRoute>} />
        {/* Data Cleanup / Transaction Review routes */}
        <Route path="/cleanup-validation" element={<ProtectedRoute><CleanupValidation /></ProtectedRoute>} />
        <Route path="/cleanup/validation/:jobId" element={<ProtectedRoute><CleanupValidation /></ProtectedRoute>} />
        <Route path="/cleanup" element={<Navigate to="/wizard-v2" replace />} />
        <Route path="/cleanup/monitoring" element={<Navigate to="/" replace />} />
        <Route path="/cleanup/history" element={<Navigate to="/" replace />} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/settings/activity" element={<ProtectedRoute><AdminActivityLog /></ProtectedRoute>} />
        <Route path="/settings/patterns" element={<ProtectedRoute><PatternAdminPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/vendor-intelligence" element={<ProtectedRoute><VendorIntelligencePage /></ProtectedRoute>} />
        <Route path="/game-theory" element={<ProtectedRoute><GameTheoryPage /></ProtectedRoute>} />
        <Route path="/settlement-intelligence" element={<ProtectedRoute><SettlementIntelligencePage /></ProtectedRoute>} />
        <Route path="/docs-guide" element={<ProtectedRoute><DocsGuidePage /></ProtectedRoute>} />
        <Route path="/review" element={<ProtectedRoute><DataReviewPage /></ProtectedRoute>} />
        <Route path="/closed-cases" element={<ProtectedRoute><ClosedCasesPage /></ProtectedRoute>} />
        <Route path="/review-queue" element={<ProtectedRoute><TransactionReviewRouter /></ProtectedRoute>} />
        <Route path="/batch-processing" element={<ProtectedRoute><BatchProcessingPage /></ProtectedRoute>} />
        
        {/* Issue Tracker Routes (Phase 3.5a) - Feature flag aware */}
        <Route path="/issues" element={<ProtectedRoute><IssueTrackerRouter /></ProtectedRoute>} />
        <Route path="/issues/:issueId" element={<ProtectedRoute><IssueDetail /></ProtectedRoute>} />
        
        {/* Orchestrator Dashboard (Phase 4) */}
        <Route path="/orchestrator" element={<ProtectedRoute><OrchestratorDashboard /></ProtectedRoute>} />
        
        {/* Adjuster Analyzer View (Phase 3.5b) */}
        <Route path="/adjuster/:reportId" element={<ProtectedRoute><AdjusterAnalyzerView /></ProtectedRoute>} />
        
        {/* Case Strengthening Roadmap (Staff View) */}
        <Route path="/roadmap/:reportId" element={<ProtectedRoute><StaffRoadmapPage /></ProtectedRoute>} />
        
        {/* Queue-Based Dashboards (Now-Line) - Feature flag aware */}
        <Route path="/queue" element={<ProtectedRoute><UnifiedWorkQueue /></ProtectedRoute>} />
        <Route path="/queue/data-specialist" element={<ProtectedRoute><DataSpecialistQueueRouter /></ProtectedRoute>} />
        <Route path="/queue/accountant" element={<ProtectedRoute><AccountantQueue /></ProtectedRoute>} />
        <Route path="/queue/attorney" element={<ProtectedRoute><React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}><AttorneyQueue /></React.Suspense></ProtectedRoute>} />
        <Route path="/staff" element={<ProtectedRoute><StaffDashboard /></ProtectedRoute>} />
        
        {/* Role-Specific Dashboards (new architecture) */}
        <Route path="/dashboard/data-specialist" element={<ProtectedRoute><DataSpecialistDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/accountant" element={<ProtectedRoute><AccountantDashboardRole /></ProtectedRoute>} />
        
        {/* Accountant Portal Routes (Separate JWT Auth - Not Clerk) */}
        <Route path="/accountant/login" element={<AccountantLogin />} />
        <Route path="/accountant/register" element={<AccountantRegister />} />
        <Route path="/accountant/dashboard" element={<Navigate to="/queue/accountant" replace />} />
        <Route path="/accountant" element={<Navigate to="/queue/accountant" replace />} />
        <Route path="/accountant/reports" element={<AccountantSharedReports />} />
        <Route path="/accountant/reports/:reportId" element={<AccountantReportView />} />
        <Route path="/accountant/report/:reportId" element={<AccountantReportView />} />
        <Route path="/accountant/comments" element={<AccountantComments />} />
        <Route path="/accountant/profile" element={<AccountantProfilePage />} />
        <Route path="/accountant/anomalies/:reportId" element={<AccountantAnomalyReview />} />
        <Route path="/accountant/reconciliation/:reportId" element={<AccountantReconciliation />} />
        <Route path="/accountant/narrator/:reportId" element={<NarratorPage />} />
        <Route path="/accountant/accept-invite/:token" element={<AcceptInvite />} />
        
        {/* Attorney Portal Routes (Separate JWT Auth - Not Clerk) */}
        <Route path="/attorney/login" element={<AttorneyLogin />} />
        <Route path="/attorney/reset-password" element={<AttorneyResetPassword />} />
        <Route path="/attorney/forgot-password" element={<AttorneyResetPassword />} />
        <Route path="/attorney/dashboard" element={<AttorneyDashboard />} />
        <Route path="/attorney" element={<AttorneyDashboard />} />
        <Route path="/attorney/case/:caseId" element={<AttorneyCaseDetail />} />
        <Route path="/attorney/case/:caseId/settlement" element={<SettlementToolsRedirect />} />
        <Route path="/attorney/case/:caseId/tools" element={<AdvancedToolsRedirect />} />
        <Route path="/attorney/case/:caseId/demand-letter" element={<DemandLetterGenerator />} />
        <Route path="/attorney/case/:caseId/timeline" element={<BITimelinePage />} />
        <Route path="/attorney/demand-letter" element={<DemandLetterGenerator />} />
        <Route path="/attorney/timeline" element={<BITimelinePage />} />
        {/* Attorney Package View - Public route with token (Phase 6) */}
        <Route path="/attorney/package/:token" element={<AttorneyPackageView />} />
        {/* Policy DNA routes removed - policy coverage now integrated into case detail page */}
        
        {/* 404 catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  // In test mode, skip ClerkProvider entirely
  if (isTestMode) {
    console.log('🧪 TEST MODE ENABLED - Auth bypassed');
    return (
      <TestAuthProvider>
        <BrandingProvider>
          <AccountantAuthProvider>
            <PermissionsProvider>
              <AppContent />
              <Toaster />
            </PermissionsProvider>
          </AccountantAuthProvider>
        </BrandingProvider>
      </TestAuthProvider>
    );
  }

  // Normal mode with Clerk
  return (
    <ClerkProvider
      publishableKey={CLERK_KEY}
      appearance={{
        elements: {
          rootBox: "mx-auto",
          card: "shadow-none"
        }
      }}
    >
      <AuthTokenRegistrar>
        <BrandingProvider>
          <AccountantAuthProvider>
            <PermissionsProvider>
              <AppContent />
              <Toaster />
            </PermissionsProvider>
          </AccountantAuthProvider>
        </BrandingProvider>
      </AuthTokenRegistrar>
    </ClerkProvider>
  );
}

export default App;
